<template>
    <div class="content-wrapper">
        <Breadcrumbs :items="breadcrumbs"></Breadcrumbs>
        <div class="content">
            <SpinnerLoader v-if="loading" />
            <div v-else>
                <v-row class="ma-0" justify="space-between" align="center">
                    <h1 class="dashboard">Схемы отправки</h1>
                    <div>
                        <v-btn
                            elevation="0"
                            color="mainNew"
                            class="white--text mr-4"
                            @click="saveSchemes"
                            :loading="saveLoading"
                        >
                            Сохранить
                        </v-btn>
                        <v-btn
                            elevation="0"
                            outlined
                            class="outlined-btn"
                            @click="cancel"
                        >
                            Отмена
                        </v-btn>
                    </div>
                </v-row>

                <!-- Секция 1: Каналы по умолчанию для триггеров и активаций -->
                <DefaultChannelsSection
                    :default-channels="defaultChannels"
                    :connected-channels="connectedChannels"
                    :schemes="schemes"
                    @update="updateDefaultChannels"
                    @select-default="handleToggleDefault"
                    class="mt-8"
                />

                <!-- Секция 2: Список всех подключенных каналов -->
                <ConnectedChannelsSection
                    :channels="connectedChannels"
                    @connect-channel="openChannelConnectionSidebar"
                    @update-channels="updateConnectedChannels"
                    @refresh="refreshChannels"
                    @toggle-default="handleToggleDefault"
                    class="mt-8"
                />

                <!-- Секция 3: Схемы каскада -->
                <SendingSchemesSection
                    :schemes="schemes"
                    :connected-channels="connectedChannels"
                    @update-scheme="updateScheme"
                    @update-scheme-type="updateSchemeType"
                    @add-cascade-scheme="addCascadeScheme"
                    @add-all-channels-scheme="addAllChannelsScheme"
                    @delete-scheme="deleteScheme"
                    @connect-channel="openChannelConnectionSidebar"
                    @toggle-default="handleToggleDefault"
                    class="mt-8"
                />
            </div>
        </div>

        <!-- Сайдбар для подключения канала -->
        <ChannelConnectionSidebar
            v-if="sidebarChannel"
            :channel="sidebarChannel"
            :visible="sidebarVisible"
            @close="closeSidebar"
        />
    </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import SpinnerLoader from '@/components/SpinnerLoader.vue'
import DefaultChannelsSection from '@/components/sendingSchemes/DefaultChannelsSection.vue'
import ConnectedChannelsSection from '@/components/sendingSchemes/ConnectedChannelsSection.vue'
import SendingSchemesSection from '@/components/sendingSchemes/SendingSchemesSection.vue'
import ChannelConnectionSidebar from '@/components/sendingSchemes/ChannelConnectionSidebar.vue'
import { useProject } from '@/hooks/useProject'
import { mapGetters } from 'vuex'

export default {
    name: 'SendingSchemes',
    components: {
        Breadcrumbs,
        SpinnerLoader,
        DefaultChannelsSection,
        ConnectedChannelsSection,
        SendingSchemesSection,
        ChannelConnectionSidebar,
    },
    data: () => ({
        loading: true,
        saveLoading: false,
        defaultChannels: {
            notifications: null,
            mailings: null,
            crmMessages: null,
        },
        connectedChannels: [],
        schemes: [],
        sidebarVisible: false,
        sidebarChannel: null,
    }),
    computed: {
        ...mapGetters(['projectById']),
        project() {
            const projectId = this.$route.params.id
            return this.projectById(projectId)
        },
        breadcrumbs() {
            return [
                {
                    text: this.project?.name || '',
                    to: `/project/${this.project?.id}`,
                },
                {
                    text: 'СХЕМЫ ОТПРАВКИ',
                    to: '',
                    disabled: true,
                },
            ]
        },
    },
    async created() {
        await this.loadData()
    },
    methods: {
        async loadData() {
            this.loading = true
            try {
                // TODO: Загрузить данные с API
                // Пока используем моковые данные
                this.initializeDefaultSchemes()
                this.loadConnectedChannels()
            } catch (error) {
                console.error('Ошибка загрузки данных:', error)
                this.$store.dispatch('callNotify', 'Ошибка при загрузке данных')
            } finally {
                this.loading = false
            }
        },
        initializeDefaultSchemes() {
            // Инициализация 3 стандартных схем
            this.schemes = [
                {
                    id: 'scheme-1',
                    name: 'Схема 1 - каскад(быстрый) - MAX->TG->WA',
                    type: 'cascade',
                    cascadeMode: 'fast',
                    expanded: false,
                    channels: [
                        { id: 'max-1', type: 'MAX', priority: 1, connected: true, phone: '+79000000001', name: 'MAX' },
                        { id: 'tg-1', type: 'Telegram', priority: 2, connected: false, name: 'Telegram' },
                        { id: 'wa-1', type: 'WhatsApp', priority: 3, connected: true, phone: '+79000000003', name: 'WhatsApp' },
                    ],
                    defaultForNotifications: true,
                    defaultForMailings: false,
                    defaultForCrmMessages: true,
                },
                {
                    id: 'scheme-2',
                    name: 'Схема 2 - каскад(аккуратный) - MAX->TG->WA',
                    type: 'cascade',
                    cascadeMode: 'accurate',
                    expanded: false,
                    channels: [
                        { id: 'max-1', type: 'MAX', priority: 1, connected: true, phone: '+79000000001', name: 'MAX' },
                        { id: 'tg-1', type: 'Telegram', priority: 2, connected: false, name: 'Telegram' },
                        { id: 'wa-1', type: 'WhatsApp', priority: 3, connected: true, phone: '+79000000003', name: 'WhatsApp' },
                    ],
                    defaultForNotifications: false,
                    defaultForMailings: true,
                    defaultForCrmMessages: false,
                },
                {
                    id: 'scheme-3',
                    name: 'Схема 3 - Во все каналы - MAX-TG-WA',
                    type: 'all-channels',
                    expanded: false,
                    channels: [
                        { id: 'max-1', type: 'MAX', selected: true, connected: true, phone: '+79000000001', name: 'MAX' },
                        { id: 'tg-1', type: 'Telegram', selected: true, connected: false, name: 'Telegram' },
                        { id: 'wa-1', type: 'WhatsApp', selected: true, connected: true, phone: '+79000000003', name: 'WhatsApp' },
                    ],
                    defaultForNotifications: false,
                    defaultForMailings: true,
                    defaultForCrmMessages: false,
                },
            ]
        },
        loadConnectedChannels() {
            // TODO: Загрузить подключенные каналы с API
            this.connectedChannels = [
                {
                    id: 'yc-1',
                    type: 'YCLIENTS',
                    name: 'YCLIENTS',
                    connected: true,
                    defaultForNotifications: false,
                    defaultForMailings: false,
                    defaultForCrmMessages: false,
                },
                {
                    id: 'max-1',
                    type: 'MAX',
                    name: 'MAX',
                    phone: '+79000000001',
                    connected: true,
                    defaultForNotifications: false,
                    defaultForMailings: false,
                    defaultForCrmMessages: false,
                },
                {
                    id: 'wa-1',
                    type: 'WhatsApp',
                    name: 'WhatsApp',
                    phone: '+79000000002',
                    connected: true,
                    defaultForNotifications: false,
                    defaultForMailings: false,
                    defaultForCrmMessages: false,
                },
                {
                    id: 'tg-1',
                    type: 'Telegram',
                    name: 'Telegram',
                    phone: '+79000000003',
                    connected: false,
                    defaultForNotifications: false,
                    defaultForMailings: false,
                    defaultForCrmMessages: false,
                },
            ]
        },
        updateDefaultChannels(channels) {
            this.defaultChannels = channels
        },
        updateConnectedChannels(channels) {
            this.connectedChannels = channels
        },
        refreshChannels() {
            this.loadConnectedChannels()
        },
        updateScheme(scheme) {
            const index = this.schemes.findIndex(s => s.id === scheme.id)
            if (index !== -1) {
                this.schemes[index] = { ...scheme }
                // Обновляем автогенерацию названия
                this.schemes[index].name = this.generateSchemeName(scheme)
            }
        },
        updateSchemeType(scheme) {
            // Обновление схемы при изменении типа или режима каскада
            const index = this.schemes.findIndex(s => s.id === scheme.id)
            if (index !== -1) {
                // Обновляем схему из переданных данных
                this.$set(this.schemes, index, { ...scheme })
                // Обновляем автогенерацию названия на основе актуальной схемы
                const updatedName = this.generateSchemeName(scheme)
                this.$set(this.schemes[index], 'name', updatedName)
            }
        },
        addCascadeScheme() {
            const newScheme = {
                id: `scheme-${Date.now()}`,
                name: '',
                type: 'cascade',
                cascadeMode: 'fast',
                expanded: true,
                channels: [],
                defaultForNotifications: false,
                defaultForMailings: false,
                defaultForCrmMessages: false,
            }
            this.schemes.push(newScheme)
            this.$nextTick(() => {
                newScheme.name = this.generateSchemeName(newScheme)
                this.updateScheme(newScheme)
            })
        },
        addAllChannelsScheme() {
            const newScheme = {
                id: `scheme-${Date.now()}`,
                name: '',
                type: 'all-channels',
                expanded: true,
                channels: this.connectedChannels.map(ch => ({
                    ...ch,
                    selected: true,
                })),
                defaultForNotifications: false,
                defaultForMailings: false,
                defaultForCrmMessages: false,
            }
            this.schemes.push(newScheme)
            this.$nextTick(() => {
                newScheme.name = this.generateSchemeName(newScheme)
                this.updateScheme(newScheme)
            })
        },
        deleteScheme(schemeId) {
            const index = this.schemes.findIndex(s => s.id === schemeId)
            if (index !== -1) {
                this.schemes.splice(index, 1)
            }
        },
        generateSchemeName(scheme) {
            const schemeNumber = this.schemes.findIndex(s => s.id === scheme.id) + 1
            let name = `Схема ${schemeNumber}`
            
            if (scheme.type === 'cascade') {
                const modeMap = {
                    fast: 'быстрый',
                    accurate: 'аккуратный',
                    custom: 'свой',
                }
                name += ` - каскад(${modeMap[scheme.cascadeMode] || 'свой'})`
                
                // Генерируем сокращения каналов по приоритетам
                const channelAbbrs = scheme.channels
                    .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                    .map(ch => {
                        const typeMap = {
                            MAX: 'MAX',
                            Telegram: 'TG',
                            WhatsApp: 'WA',
                        }
                        const base = typeMap[ch.type] || ch.type
                        // Если есть несколько каналов одного типа, добавляем номер
                        const sameTypeCount = scheme.channels.filter(c => c.type === ch.type).length
                        if (sameTypeCount > 1) {
                            const sameTypeIndex = scheme.channels
                                .filter(c => c.type === ch.type)
                                .findIndex(c => c.id === ch.id) + 1
                            return `${base}${sameTypeIndex}`
                        }
                        return base
                    })
                
                if (channelAbbrs.length > 0) {
                    name += ` - ${channelAbbrs.join('->')}`
                }
            } else if (scheme.type === 'all-channels') {
                name += ' - Во все каналы'
                
                const selectedChannels = scheme.channels.filter(ch => ch.selected)
                const channelAbbrs = selectedChannels.map(ch => {
                    const typeMap = {
                        MAX: 'MAX',
                        Telegram: 'TG',
                        WhatsApp: 'WA',
                    }
                    const base = typeMap[ch.type] || ch.type
                    const sameTypeCount = selectedChannels.filter(c => c.type === ch.type).length
                    if (sameTypeCount > 1) {
                        const sameTypeIndex = selectedChannels
                            .filter(c => c.type === ch.type)
                            .findIndex(c => c.id === ch.id) + 1
                        return `${base}${sameTypeIndex}`
                    }
                    return base
                })
                
                if (channelAbbrs.length > 0) {
                    name += ` - ${channelAbbrs.join('-')}`
                }
            }
            
            return name
        },
        openChannelConnectionSidebar(channel) {
            this.sidebarChannel = channel
            this.sidebarVisible = true
        },
        closeSidebar() {
            this.sidebarVisible = false
            this.sidebarChannel = null
        },
        async saveSchemes() {
            this.saveLoading = true
            try {
                // TODO: Сохранить данные через API
                console.log('Сохранение схем:', {
                    defaultChannels: this.defaultChannels,
                    schemes: this.schemes,
                })
                this.$store.dispatch('callNotify', 'Схемы отправки сохранены')
                this.$router.push(`/project/${this.project.id}`)
            } catch (error) {
                console.error('Ошибка сохранения:', error)
                this.$store.dispatch('callNotify', 'Ошибка при сохранении схем')
            } finally {
                this.saveLoading = false
            }
        },
        cancel() {
            this.$router.push(`/project/${this.project.id}`)
        },
        handleToggleDefault({ source, sourceId, type, value }) {
            // Глобальное управление переключателями "по умолчанию"
            const propName = `defaultFor${type.charAt(0).toUpperCase() + type.slice(1)}`
            const channelTypeMap = {
                notifications: 'notifications',
                mailings: 'mailings',
                crmMessages: 'crmMessages',
            }
            const defaultChannelKey = channelTypeMap[type]
            
            if (value) {
                // Включаем переключатель - отключаем все остальные
                if (source === 'channel') {
                    // Отключаем во всех каналах
                    this.connectedChannels.forEach(ch => {
                        if (ch.id !== sourceId) {
                            this.$set(ch, propName, false)
                        } else {
                            this.$set(ch, propName, true)
                        }
                    })
                    // Отключаем во всех схемах
                    this.schemes.forEach(scheme => {
                        this.$set(scheme, propName, false)
                    })
                    // Обновляем defaultChannels
                    if (defaultChannelKey) {
                        this.$set(this.defaultChannels, defaultChannelKey, `channel-${sourceId}`)
                    }
                } else if (source === 'scheme') {
                    // Отключаем во всех схемах
                    this.schemes.forEach(scheme => {
                        if (scheme.id !== sourceId) {
                            this.$set(scheme, propName, false)
                        } else {
                            this.$set(scheme, propName, true)
                        }
                    })
                    // Отключаем во всех каналах
                    this.connectedChannels.forEach(ch => {
                        this.$set(ch, propName, false)
                    })
                    // Обновляем defaultChannels
                    if (defaultChannelKey) {
                        this.$set(this.defaultChannels, defaultChannelKey, `scheme-${sourceId}`)
                    }
                }
            } else {
                // Выключаем переключатель
                if (source === 'channel') {
                    const channel = this.connectedChannels.find(ch => ch.id === sourceId)
                    if (channel) {
                        this.$set(channel, propName, false)
                    }
                    // Очищаем defaultChannels, если это был выбранный канал
                    if (defaultChannelKey && this.defaultChannels[defaultChannelKey] === `channel-${sourceId}`) {
                        this.$set(this.defaultChannels, defaultChannelKey, null)
                    }
                } else if (source === 'scheme') {
                    const scheme = this.schemes.find(s => s.id === sourceId)
                    if (scheme) {
                        this.$set(scheme, propName, false)
                    }
                    // Очищаем defaultChannels, если это была выбранная схема
                    if (defaultChannelKey && this.defaultChannels[defaultChannelKey] === `scheme-${sourceId}`) {
                        this.$set(this.defaultChannels, defaultChannelKey, null)
                    }
                }
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.content-wrapper
    padding-top: 80px

.content
    max-width: 1016px
    margin: 0 auto
    padding: 24px
</style>
